import React, { useEffect, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import FlashAlert from "../../Components/FlashAlert/FlashAlert";
import RegistrationFieldsInput from "../../Components/RegistrationFieldsInput";
import { Validator as CFValidator } from "@marketto/codice-fiscale-utils/dist/codice-fiscale-utils";
import apiService from "../../services/api.service";
import { useApplication } from "../../context/ApplicationContext";
import { ROLE_LABELS, ROLES, getRole } from "../../utils/roles";
import { answersFromUser, areAnswersValid } from "../../utils/registrationFields";
import { updateStoredFullname } from "../../utils/session";

const DashboardProfile = ({ loginStatus, setLoginStatus }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [email, setEmail] = useState("");
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [extraValues, setExtraValues] = useState({});
  const { application } = useApplication();
  const extraFields = useMemo(
    () => application?.registration_fields || [],
    [application]
  );

  const role = getRole(loginStatus);
  const showExtraFields = role === ROLES.UTENTE_BASE || extraFields.length > 0;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await apiService.getCurrentUser();
        const user = response.data;
        setEmail(user.username || "");
        setFirstname(user.first_name || "");
        setLastname(user.last_name || "");
        setExtraValues(answersFromUser(user, extraFields));
      } catch (e) {
        setError("Impossibile caricare il profilo.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [extraFields]);

  const extraValid = areAnswersValid(extraFields, extraValues, {
    codiceFiscaleValid: (value) => CFValidator.codiceFiscale(value).valid,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setSaving(true);
    try {
      const response = await apiService.updateMyProfile({
        first_name: firstname,
        last_name: lastname,
        custom_fields: extraValues,
        croas: extraValues.croas,
        codice_fiscale: extraValues.codice_fiscale,
        provincia: extraValues.provincia,
      });
      const updatedUser = response.data || {};
      const fullname = `${updatedUser.first_name ?? firstname} ${updatedUser.last_name ?? lastname}`.trim();
      updateStoredFullname(fullname);
      if (setLoginStatus) {
        setLoginStatus((current) => ({ ...current, fullname }));
      }
      setSuccess("Profilo aggiornato con successo.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" && detail.trim() ? detail : "Salvataggio non riuscito. Verifica i dati inseriti.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dashboard-card">
      <h1 className="dashboard-card__title">Il mio profilo</h1>
      <p className="dashboard-card__subtitle">
        Leggi e modifica i tuoi dati. Email, ruolo e password non sono modificabili.
      </p>
      {error && <FlashAlert variant="danger" onHidden={() => setError("")}>{error}</FlashAlert>}
      {success && <FlashAlert variant="success" onHidden={() => setSuccess("")}>{success}</FlashAlert>}

      {loading ? (
        <p>Caricamento profilo...</p>
      ) : (
        <Form onSubmit={handleSubmit} className="login-form">
          <Form.Group className="mb-3">
            <Form.Label>Nome</Form.Label>
            <Form.Control
              required
              value={firstname}
              onChange={(e) => setFirstname(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Cognome</Form.Label>
            <Form.Control
              required
              value={lastname}
              onChange={(e) => setLastname(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control value={email} disabled readOnly />
            <Form.Text className="text-muted">L&apos;email non è modificabile.</Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Ruolo</Form.Label>
            <Form.Control value={ROLE_LABELS[role]} disabled readOnly />
          </Form.Group>
          {showExtraFields && (
            <RegistrationFieldsInput
              fields={extraFields}
              values={extraValues}
              onChange={setExtraValues}
              idPrefix="profile"
            />
          )}
          <Button
            type="submit"
            className="users-edit-btn"
            disabled={saving || !extraValid}
          >
            {saving ? "Salvataggio..." : "Salva modifiche"}
          </Button>
        </Form>
      )}
    </div>
  );
};

export default DashboardProfile;
